import { useEffect, useMemo, useRef, useState } from "react";

export const APPROVED_FOOD_IMAGES = [
  {
    src: "/customer-final/raspberry-panna-cotta.jpg",
    alt: "Raspberry panna cotta with pistachios and mint",
  },
  {
    src: "/customer-final/burger.jpg",
    alt: "Double cheeseburger with tomato, red onion, lettuce and fries",
  },
  {
    src: "/customer-final/tacos.webp",
    alt: "Steak and chicken tacos with onions, cilantro, lime and jalapeño",
  },
];

function isOwnerManagedImage(src) {
  const value = String(src || "");
  return value.startsWith("/api/cms-media/")
    || value.startsWith("/customer-final/")
    || value.startsWith("/customer-hero/");
}

export default function HeroFoodCarousel({ images = [], videoSrc = "", fallbackImage = "" }) {
  const slides = useMemo(() => {
    const ownerImages = Array.isArray(images)
      ? images.filter(isOwnerManagedImage).slice(0, 5)
      : [];
    if (!ownerImages.length) return Array.isArray(images) && images.length === 0 ? [] : APPROVED_FOOD_IMAGES;
    return ownerImages.map((src, index) => ({
      src,
      alt: `Featured Arctic Plate food photo ${index + 1}`,
    }));
  }, [images]);

  const [active, setActive] = useState(0);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);
  const connection = typeof navigator !== "undefined" ? navigator.connection : null;
  const videoAllowed = typeof window === "undefined" || !(
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
    || connection?.saveData
    || ["slow-2g", "2g"].includes(String(connection?.effectiveType || "").toLowerCase())
  );

  // Photos are a fallback, not a temporary poster while the video buffers.
  // Keep the neutral background until a frame is ready to prevent food-image flashes.
  const showPhotoFallback = !videoSrc || !videoAllowed || videoFailed;

  useEffect(() => {
    setActive(0);
  }, [slides.length]);

  useEffect(() => {
    setVideoFailed(false);
    setVideoReady(false);
  }, [videoSrc]);

  useEffect(() => {
    if (slides.length <= 1 || (videoSrc && videoAllowed && !videoFailed)) return undefined;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [slides.length, videoSrc, videoAllowed, videoFailed]);

  useEffect(() => {
    if (!videoSrc || !videoAllowed || videoFailed) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const resume = () => {
      if (video.ended) video.currentTime = 0;
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {});
      }
    };
    const onVisibilityChange = () => {
      if (!document.hidden) resume();
    };

    resume();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("focus", resume);
    };
  }, [videoSrc, videoAllowed, videoFailed]);

  return (
    <div
      className="absolute inset-0 h-full w-full overflow-hidden bg-[#151719]"
      data-testid="hero-food-carousel"
      role="region"
      aria-label="Featured food photography"
    >
      {showPhotoFallback && fallbackImage && <img src={fallbackImage} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center" data-testid="hero-fallback-image" />}
      {showPhotoFallback && slides.map((image, index) => (
        <div key={`${image.src}-${index}`} data-testid={`hero-food-slide-${index}`} className={`absolute inset-0 h-full w-full transition-opacity duration-1000 ${active === index ? "opacity-100" : "pointer-events-none opacity-0"}`}>
          <img src={image.src} alt={image.alt} loading={index === 0 ? "eager" : "lazy"} decoding="async" className={`h-full w-full object-cover object-center ${active === index ? "ap-hero-drift" : ""}`} />
        </div>
      ))}
      {videoSrc && videoAllowed && !videoFailed ? (
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${videoReady ? "opacity-100" : "opacity-0"}`}
          aria-hidden="true"
          data-testid="hero-food-video"
          onLoadedData={() => setVideoReady(true)}
          onCanPlay={(event) => {
            setVideoReady(true);
            const playAttempt = event.currentTarget.play();
            if (playAttempt && typeof playAttempt.catch === "function") playAttempt.catch(() => {});
          }}
          onPause={(event) => {
            if (typeof document !== "undefined" && document.hidden) return;
            const playAttempt = event.currentTarget.play();
            if (playAttempt && typeof playAttempt.catch === "function") playAttempt.catch(() => {});
          }}
          onEnded={(event) => {
            event.currentTarget.currentTime = 0;
            const playAttempt = event.currentTarget.play();
            if (playAttempt && typeof playAttempt.catch === "function") playAttempt.catch(() => {});
          }}
          onError={() => setVideoFailed(true)}
        />
      ) : null}
    </div>
  );
}

